'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import 'tailwind-merge';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';
import List from '../List/List.js';
import SupportTextContainer from '../SupportTextContainer/SupportTextContainer.js';
import useDropdown from '../../../hooks/useDropdown.js';
import { SelectBox } from './TextFieldDropdown.parts.js';

/**
 * 드롭다운 형태의 텍스트 필드 컴포넌트입니다.
 *
 * @component
 *
 * @param {Object} props
 * @param {Item[]} props.items - 드롭다운에 표시될 아이템 배열 ({key, value, leadingIcon}[])
 * @param {string} props.placeholder - 선택되지 않은 상태에서 표시될 안내 텍스트
 * @param {(item: Item) => void} props.onItemClick - 아이템 선택 시 호출되는 콜백 함수
 * @param {Item} [props.selectedItem] - 현재 선택된 아이템
 * @param {boolean} [props.disabled] - 비활성화 여부
 * @param {string} [props.supportMessage] - 입력 필드 아래에 표시될 도움말 메시지
 * @param {string} [props.errorMessage] - 오류 발생 시 표시될 메시지
 * @param {string} [props.className] - 추가적인 CSS 클래스명
 * @param {boolean} [props.isError] - 오류 상태 여부
 *
 * @example
 * ```tsx
 * const items = [
 *   { key: 1, value: '옵션 1', leadingIcon: 'home' },
 *   { key: 2, value: '옵션 2', leadingIcon: 'settings' }
 * ];
 *
 * <TextFieldDropdown
 *   items={items}
 *   placeholder="옵션을 선택하세요"
 *   onItemClick={(item) => console.log('선택된 아이템:', item)}
 *   selectedItem={items[0]}
 *   supportMessage="추가 설명이 필요한 경우 이곳에 표시됩니다"
 * />
 * ```
 */ const TextFieldDropdown = ({ selectedItem, items, onItemClick, placeholder, disabled, isError, errorMessage, supportMessage, className })=>{
    const { isListOpen, focusedIndex, ref: listRef, handleItemClick, toggleListOpen, handleKeyDown } = useDropdown({
        items,
        onItemClick,
        disabled
    });
    const isVisibleSupportContainer = !!supportMessage || !!errorMessage;
    return /*#__PURE__*/ jsxs("div", {
        "data-testid": "design-system--textFieldDropdown",
        ref: listRef,
        className: "relative flex w-full flex-col gap-2 outline-none",
        onKeyDown: handleKeyDown,
        tabIndex: 0,
        children: [
            /*#__PURE__*/ jsx(SelectBox, {
                placeholder: placeholder,
                value: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value,
                leadingIcon: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.leadingIcon,
                disabled: disabled,
                isListOpen: isListOpen,
                onClick: toggleListOpen,
                error: isError,
                className: className
            }),
            isVisibleSupportContainer && /*#__PURE__*/ jsx(SupportTextContainer, {
                errorMessage: errorMessage,
                supportMessage: supportMessage
            }),
            isListOpen && /*#__PURE__*/ jsx(Box, {
                className: "absolute top-[54px] z-40 w-full",
                children: /*#__PURE__*/ jsx(List.Root, {
                    children: items.map((item, index)=>/*#__PURE__*/ jsx(List.Item, {
                            text: item.value,
                            leadingIcon: item.leadingIcon,
                            onClick: ()=>handleItemClick(item),
                            selected: item.key === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.key),
                            isFocused: index === focusedIndex
                        }, item.key))
                })
            })
        ]
    });
};

export { TextFieldDropdown as default };
